<template>
    <application-modal v-model="infoModal">
        <v-btn
            data-testid="update-tenant-modal-trigger-btn"
            class="update-info-modal-trigger-btn info large pl-3 pr-4"
            outlined
            slot="modal-trigger"
            @click.stop="infoModal = true"
        >
            <v-icon class="mr-3">account_box</v-icon>
            Update Tenant Info
        </v-btn>
        <div slot="modal-title">
            Update Tenant Info
        </div>
        <div slot="modal-body">
          <validation-observer ref="observer">
            <v-layout>
              <v-btn
                data-testid="update-tenant-auto-fill-btn"
                class="update-tenant-auto-fill-btn px-3 mb-3 info"
                large
                outlined
                @click="autoFill"
              >
                <v-icon class="mr-3">transform</v-icon>
                Auto-fill Fields
              </v-btn>
              <v-checkbox
                data-testid="update-business-customer-checkbox"
                class="update-business-customer-checkbox mt-2 pt-2"
                @change="showCompanyNameField"
                v-model="payload.is_business"
              >
                <template v-slot:label>
                  <div class="subheading">Business Customer</div>
                </template>
              </v-checkbox>
            </v-layout>
            <v-layout>
                <app-input
                    data-testid="update-tenant-first-name-input"
                    class="update-tenant-first-name-input px-2"
                    v-model="payload.first_name"
                    filled
                    label="First Name"
                ></app-input>
                <app-input
                    data-testid="update-tenant-last-name-input"
                    class="update-tenant-last-name-input px-2"
                    v-model="payload.last_name"
                    filled
                    label="Last Name"
                ></app-input>
            </v-layout>
            <v-layout>
                <app-input
                    data-testid="update-tenant-address-one-input"
                    class="update-tenant-address-one-input px-2"
                    v-model="payload.address1"
                    filled
                    label="Address 1"
                ></app-input>
                <app-input
                    data-testid="update-tenant-address-two-input"
                    class="update-tenant-address-two-input px-2"
                    v-model="payload.address2"
                    filled
                    label="Address 2"
                    :useValidation="false"
                ></app-input>
            </v-layout>
            <v-layout>
                <app-input
                    data-testid="update-tenant-city-input"
                    class="update-tenant-city-input px-2"
                    v-model="payload.city"
                    filled
                    label="City"
                ></app-input>
                <validation-provider
                  rules="required"
                  v-slot="{ valid, errors, classes }"
                  mode="eager"
                >
                  <v-select
                      data-testid="update-tenant-state-menu"
                      class="update-tenant-state-menu px-2"
                      :class="classes"
                      v-model="payload.state"
                      :error-messages="errors"
                      :success="valid"
                      filled
                      label="State"
                      :items="states"
                      item-text="value"
                  />
              </validation-provider>
                <app-input
                    data-testid="update-tenant-postal-code-input"
                    class="update-tenant-postal-code-input px-2"
                    filled
                    label="Postal Code"
                    v-model="payload.zip"
                    :rules="'required|digits:5'"
                ></app-input>
            </v-layout>
            <v-layout>
                <app-input
                    data-testid="update-tenant-phone-input"
                    class="update-tenant-phone-input px-2"
                    v-model="payload.phone"
                    filled
                    label="Phone Number"
                    type="phone"
                    :rules="{ required: true, phone: true }"
                ></app-input>
                <app-input
                    data-testid="update-tenant-email-input"
                    class="update-tenant-email-input px-2"
                    v-model="payload.email"
                    filled
                    label="Email Address"
                    type="email"
                    :rules="'required'"
                ></app-input>
            </v-layout>
            <v-layout v-if="payload.is_business">
              <v-flex xs6>
                <app-input
                  data-testid="update-tenant-company-name-input"
                  class="update-tenant-company-name-input px-2"
                  v-model="payload.business_name"
                  filled
                  label="Company Name"
                ></app-input>
              </v-flex>
            </v-layout>
            <v-layout>
                <validation-provider
                  rules="required"
                  v-slot="{ valid, errors, classes }"
                  mode="eager"
                >
                  <v-select
                      data-testid="update-tenant-id-type"
                      class="update-tenant-id-type px-2"
                      :class="classes"
                      v-model="payload.id_type"
                      :error-messages="errors"
                      :success="valid"
                      filled
                      label="ID Types"
                      :items="idTypeOptions"
                      item-text="label"
                      item-value="value"
                  />
              </validation-provider>
                <app-input
                  data-testid="update-tenant-id-number"
                  class="update-tenant-id-number-input px-2"
                  v-model="payload.id_number"
                  filled
                  label="ID Number"
                ></app-input>
                <validation-provider
                  v-if="!isPassport"
                  rules="required"
                  v-slot="{ valid, errors, classes }"
                  mode="eager"
                >
                  <v-select
                    data-testid="update-tenant-id-state"
                    class="update-tenant-id-state-input px-2"
                    :label="`State (${payload.id_type === 1 ? 'Driver\'s License Number' : 'State ID Number'})`"
                    :items="states"
                    :class="classes"
                    v-model="payload.id_state"
                    :error-messages="errors"
                    :success="valid"
                    filled
                    :rules="[(v) => !!v || 'This field is required']"
                    item-text="value"
                  />
                </validation-provider>
            </v-layout>
          </validation-observer>
        </div>
        <v-btn
            data-testid="update-tenant-submit-btn"
            class="update-tenant-submit-btn primary"
            slot="create-button"
            @click="submitUpdate"
            :loading="loading"
            :disabled="invalidFields"
        >
          Submit Update
        </v-btn>
    </application-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ApplicationModal from "@/components/ApplicationModal";
import states from "@/helpers/states";

export default {
  name: "UpdateInfo",
  props: ["tenant"],
  data() {
    return {
      infoModal: false,
      loading: false,
      states: states,
      idTypeOptions: [
        { label: "Driver's License Number", value: 1 },
        { label: "State ID Number", value: 2 },
        { label: "Passport Number", value: 3 }
      ],
      payload: {
        first_name: "",
        last_name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        email: "",
        id_number: "",
        id_type: 1,
        id_state: null,
        business_name: null,
        is_business: false,
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      }
    };
  },
  methods: {
    autoFill() {
      this.payload.first_name = this.tenant.first_name;
      this.payload.last_name = this.tenant.last_name;
      this.payload.address1 = this.tenant.street_address;
      this.payload.address2 = this.tenant.street_address_2;
      this.payload.city = this.tenant.city;
      this.payload.state = this.tenant.state;
      this.payload.zip = this.tenant.postal_code;
      this.payload.phone = this.addDashes(this.tenant.phone_number);
      this.payload.email = this.tenant.email;
      this.payload.id_number = this.tenant.id_number;
      this.payload.id_type = parseInt(this.tenant.id_type, 10);
      this.payload.id_state = this.tenant.id_state ? this.tenant.id_state : this.tenant.state;
      this.payload.business_name = this.companyTenant.is_business ? this.companyTenant.business_name : this.tenant.business_name;
      this.payload.is_business = this.companyTenant.bCompanyIsTenant ? this.companyTenant.bCompanyIsTenant : this.tenant.is_business;
    },

    submitUpdate() {
      this.$refs.observer.validate().then(formIsValidated => {
        if (formIsValidated) {
          this.$store.dispatch("handleResetCompanyInfo");
          this.loading = true;
          if (this.isTypeIdPassport(this.payload.id_type)) {
            this.payload.id_state = null;
          }
          this.$store.dispatch("updateTenantInfo", this.payload)
            .then(() => {
              this.loading = false;
              this.infoModal = false;
              this.$store.dispatch("handleSuccess", "Tenant information successfully updated!");
              this.$emit("success");
            })
            .catch(err => {
              this.$store.dispatch("handleError", err);
              this.loading = false;
            });
        }
      });
    },

    isTypeIdPassport(idType) {
      return parseInt(idType, 10) === 3;
    },

    addDashes(phone) {
      if (phone) {
        if (phone.length === 10 && typeof phone.replace === "function") {
          return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        } else {
          return phone;
        }
      }
    },

    showCompanyNameField() {
      if (!this.payload.is_business) {
        this.payload.business_name = null;
      }
    }
  },
  computed: {
    isPassport() {
      return parseInt(this.payload.id_type, 10) === 3;
    },
    companyTenant() {
      return this.$store.state.tenant.companyInfo;
    },
    invalidFields() {
      let regex = /^\S+@\S+\.\S+$/;
      if (this.payload.phone && this.payload.phone.length !== 12) {
        return true;
      } else if (this.payload.zip && this.payload.zip.length !== 5) {
        return true;
      } else if (this.payload.email && !regex.test(this.payload.email)) {
        return true;
      } else if (this.payload.is_business && !this.payload.business_name) {
        return true;
      } else {
        return false;
      };
    }
  },
  components: {
    ApplicationModal,
    ValidationObserver,
    ValidationProvider
  }
};
</script>
